import React, { useRef, useState } from "react";
import * as Dialog from "@/components/ui/modal/Dialog.js";
import { ChevronLeft } from "lucide-react";
import {
  AgenUserSubscriptionType,
  agentUserSubscriptionHooks,
} from "@/manager/manager/agent-user-subscription-manager";
import { agentUserTemplateListHooks, materialSettingsUpdateEvent } from "@/manager/manager/agent-user-template-list-manager";
import { agentUserMaterialsHooks } from "@/manager/manager/agent-user-materials-manager";
import { agentUserVoiceListHooks } from "@/manager/manager/agent-user-voice-list-manager";
import { PushSettingsReadonly } from "./push-settings-readonly";
import { TemplatesCarousel, TemplatesCarouselAction } from "./templates-carousel";
import { MaterialsCarousel } from "./materials-carousel";
import { PanelAgentTemplate } from "./panel-agent-template";
import { PanelAgentMaterialLibrary } from "./panel-agent-material-libary";
import { VoicesCarousel } from "./voices-carousel";
import { useTranslation } from "react-i18next";
import { PUSH_SETTINGS_TYPE } from "@/manager/manager/agent-dashboard-user-management-manager";
import { ArticleSetting, ArticleSettingAction } from "./article-settings";
import { agentsHooks, agentUserKnowledgeHooks } from "@/manager";
import { TextButton } from "../common/text-button";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { KnowledgeCarousel } from "./knowledge-carousel";
import { ModalCloseButton } from "../common/modal-close-button";
import { PanelAgentKnowledgeLibrary } from "./panel-agent-knowledge-library";
import { PanelAgentVoice } from "./panel-agent-voice";

export interface PanelAgentSubscriptionSettingsProps {
  open: boolean;
  agentId?: string;
  type?: "video" | "podcast" | "moments" | "post" | "article";
  pushSettings?: { [key: number]: number };
  onOpenChange: (open: boolean) => void;
}

export const PanelAgentSubscriptionSettings: React.FC<
  PanelAgentSubscriptionSettingsProps
> = ({ open, onOpenChange, agentId, type, pushSettings }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const obj = {
    video: AgenUserSubscriptionType.video,
    podcast: AgenUserSubscriptionType.podcast,
    moments: AgenUserSubscriptionType.moments,
    post: AgenUserSubscriptionType.post,
    article: AgenUserSubscriptionType.article,
  };
  const tabNameMap: Record<AgenUserSubscriptionType, string> = {
    video: t("agent_creation.subscription_settings.video"),
    podcast: t("agent_creation.subscription_settings.podcast"),
    moments: t("agent_creation.subscription_settings.moments"),
    post: t("agent_creation.subscription_settings.post"),
    article: t("agent_creation.subscription_settings.article"),
  };
  const currentType = obj[type];
  const title = tabNameMap[currentType];
  const [templateOpen, setTemplateOpen] = useState(false);
  const [materialOpen, setMaterialOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [voicesOpen, setVoicesOpen] = useState(false);
  const templatesCarouselRef = useRef<TemplatesCarouselAction>(null);
  const [videoTemplateDetailLoading, setVideoTemplateDetailLoading] = useState(false);
  const subscriptionInfo =
    agentUserSubscriptionHooks.useGetAgentUserSubscriptionInfo(
      agentId,
      currentType
    );
  const { templates, loading: templatesLoading, refresh: refreshTemplates } = agentUserTemplateListHooks.useTemplateListData({
    agentId,
    type: currentType,
  });
  const {
    folders: materials,
    loading: materialsLoading,
    updateMaterials,
    updateLoading: materialsUpdateLoading,
  } = agentUserMaterialsHooks.useFolderListData({
    agentId,
    type: currentType,
  });
  const {
    folders: knowledge,
    loading: knowledgeLoading,
    updateKnowledge,
    updateLoading: knowledgeUpdateLoading,
    addKnowledgeFolders,
    removeKnowledgeFolders,
  } = agentUserKnowledgeHooks.useFolderListData({
    agentId,
    type: currentType,
  });
  const { templates: voiceTemplates, loading: voicesLoading, saveVoiceTemplate, removeVoiceTemplate, saveLoading: saveVoiceLoading } = agentUserVoiceListHooks.useVoiceListData({
    agentId,
    type: currentType,
  });
  const { agentUserActors, agentUserVoices, loading: agentUserLoading } = agentUserVoiceListHooks.useUserActorAndVoiceData();
  // 获取/保存智能体文章设置（页眉,底图）
  const { articleSettings, saveAgentArticleSettings, saveLoading, uploadArticleSettingImage } = agentsHooks.useAgentArticleSettings();
  const articleSettingRef = React.useRef<ArticleSettingAction>(null);
  const [prepareSave, setPrepareSave] = useState(false);

  const handleSaveArticleSettings = async () => {
    if (!articleSettingRef.current) return;
    const data = articleSettingRef.current.getData();
    await saveAgentArticleSettings({
      articleHeaderCopy: data.articleHeaderCopy,
      articleDrainageImage: data.articleDrainageImage,
    });
    onOpenChange(false);
    setPrepareSave(false);
    toast.default({
      description: t("agent_creation.subscription_settings.save_success"),
    });
  }
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          id="miuta-dialog"
          className="
            fixed right-0 top-0 z-50 h-full w-[480px]
            bg-white
            flex flex-col
            data-[state=open]:translate-x-0 data-[state=closed]:translate-x-full
            transition-transform duration-300
          "
        >
          {/* 顶栏 */}
          <div className="flex items-center justify-start mt-6 px-6 gap-4">
            <ModalCloseButton
              icon={<ChevronLeft className="w-5 h-5" />}
              onClick={() => onOpenChange(false)}
            />
            <div className="text-base text-[rgba(16,24,40,1)] font-medium">
              {title}
            </div>
            <div className="w-6" />
          </div>

          {/* 内容区 */}
          <div className="flex flex-col flex-1 overflow-auto mt-6 px-6 gap-6">
            <PushSettingsReadonly
              subscribed={subscriptionInfo?.subscribed}
              pushFrequency={subscriptionInfo?.pushFrequency}
              pushQuantity={subscriptionInfo?.pushQuantity}
              pushTime={subscriptionInfo?.pushTime}
              showPushSettings={currentType !== 'post'
                ? pushSettings[PUSH_SETTINGS_TYPE[currentType]] === 1
                : pushSettings[4] === 1 // 图文特殊处理
              }
            />
            <div className="mb-10">
              <div className="text-base font-medium text-[rgba(16,24,40,1)] mb-5">
                {t("agent_creation.subscription_settings.content")}
              </div>
              {/* 文章设置(页眉,底图) */}
              {currentType === AgenUserSubscriptionType.article && (
                <ArticleSetting
                  ref={articleSettingRef}
                  articleHeaderCopy={articleSettings?.articleHeaderCopy || ''}
                  articleDrainageImage={articleSettings?.articleDrainageImage || ''}
                  uploadArticleSettingImage={uploadArticleSettingImage}
                  onDataChange={(changed) => {
                    setPrepareSave(changed);
                  }}
                />
              )}
              {currentType !== AgenUserSubscriptionType.podcast && currentType !== AgenUserSubscriptionType.moments && (
                <div className="rounded-xl mb-6">
                  <TemplatesCarousel
                    ref={templatesCarouselRef}
                    templates={templates}
                    agentUserActors={agentUserActors}
                    voiceTemplates={agentUserVoices}
                    showVideoAction={currentType === AgenUserSubscriptionType.video}
                    loading={templatesLoading}
                    agentUserLoading={agentUserLoading}
                    onOpenAll={() => {
                      setTemplateOpen(true);
                    }}
                    onCreateSuccess={() => {
                      refreshTemplates();
                    }}
                    onDetailLoadingChange={(loading: boolean) => {
                      setVideoTemplateDetailLoading(loading);
                    }}
                  />
                </div>
              )}
              {currentType === AgenUserSubscriptionType.podcast && (
                <VoicesCarousel
                  templates={voiceTemplates}
                  loading={voicesLoading}
                  saveLoading={saveVoiceLoading}
                  onSaveVoiceTemplate={saveVoiceTemplate}
                  onRemoveVoiceTemplate={removeVoiceTemplate}
                  onOpenAll={() => {
                    setVoicesOpen(true);
                  }}
                />
              )}
              {currentType !== AgenUserSubscriptionType.podcast && currentType !== AgenUserSubscriptionType.moments && (
                <div className="rounded-xl mb-6">
                  <MaterialsCarousel
                    materials={materials}
                    loading={materialsLoading}
                    onOpenAll={() => {
                      setMaterialOpen(true);
                    }}
                  />
                </div>
              )}
              <div className="rounded-xl">
                <KnowledgeCarousel
                  knowledge={knowledge}
                  loading={knowledgeLoading}
                  onOpenAll={() => {
                    setKnowledgeOpen(true);
                  }}
                />
              </div>
            </div>
          </div>

          {/* 保存按钮 */}
          {currentType === AgenUserSubscriptionType.article && (
            <div className="flex items-center justify-end gap-2 px-6 pt-3 pb-6">
              <TextButton
                label={t('agent_dashboard.confirm_modal.cancel')}
                className={cn(
                  '!w-20 h-10 bg-white',
                  'font-semibold text-sm !text-[rgba(65,70,81,1)]',
                  'border border-[rgba(213,215,218,1)] hover:bg-gray-50'
                )}
                onClick={() => onOpenChange(false)}
              />
              <TextButton
                label={t('agent_dashboard.confirm_modal.save')}
                className={cn('!w-[4.063rem] h-10', 'font-semibold text-sm text-white', 'border-none')}
                onClick={handleSaveArticleSettings}
                loading={saveLoading}
                disabled={!prepareSave || saveLoading}
              />
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
      {templateOpen && (
        <PanelAgentTemplate
          open={templateOpen}
          templates={templates}
          detailLoading={videoTemplateDetailLoading}
          loading={templatesLoading}
          onOpenChange={setTemplateOpen}
          showVideoAction={currentType === AgenUserSubscriptionType.video}
          onDelete={async (uuid) => {
            await templatesCarouselRef.current?.onDelete(uuid);
          }}
          onEdit={(uuid) => {
            templatesCarouselRef.current?.onEdit(uuid);
          }}
        />
      )}
      {materialOpen && (
        <PanelAgentMaterialLibrary
          open={materialOpen}
          materials={materials}
          onOpenChange={setMaterialOpen}
          onAddMaterial={async (ids, isAdd) => {
            const success = await updateMaterials(ids, isAdd)
            // 更新素材成功后，要刷新模板范围配置数据 
            if (success) {
              materialSettingsUpdateEvent.dispatch();
            }
            return success;
          }}
          saveLoading={materialsUpdateLoading}
        />
      )}
      {knowledgeOpen && (
        <PanelAgentKnowledgeLibrary
          open={knowledgeOpen}
          knowledge={knowledge}
          onOpenChange={setKnowledgeOpen}
          onAddKnowledge={updateKnowledge}
          onAddKnowledgeFolders={addKnowledgeFolders}
          onRemoveKnowledgeFolders={removeKnowledgeFolders}
          saveLoading={knowledgeUpdateLoading}
        />
      )}
      {voicesOpen && (
        <PanelAgentVoice
          open={voicesOpen}
          templates={voiceTemplates}
          onOpenChange={setVoicesOpen}
          saveLoading={saveVoiceLoading}
          onSaveVoiceTemplate={saveVoiceTemplate}
          onRemoveVoiceTemplate={removeVoiceTemplate}
        />
      )}
    </Dialog.Root>
  );
};
